import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';
import { APP_NAME_PULSE_WEB } from 'solaris-lens-common-lib/src/constants';
import {
  getMeProfile,
  updateUser as updateUserProfileServ
} from 'solaris-lens-common-lib/src/services/users';
import delay from 'solaris-lens-common-lib/src/utils';

import { fetchAuditableElements } from './auditableElementsSlice';
import { addUserToAuth } from './authSlice';
import { UserProfile, ApiError, AuthState } from './types';
import { getErrorObject } from './utils';
import { POOL_NAME } from '@/constants';
import { mapMeProfileToUserProfile } from '@/utils/mapMeProfileToUserProfile';

export const fetchUserProfile = createAsyncThunk(
  'userProfile/get',
  async (
    {
      includeFleetIds = false,
      allApps = true
    }: {
      includeFleetIds?: boolean;
      allApps?: boolean;
    },
    { getState, rejectWithValue, dispatch }
  ) => {
    const state = getState() as { auth: { accessToken: string } };
    const token = state.auth.accessToken;
    try {
      const responseV3 = await getMeProfile(token, includeFleetIds, allApps);
      const data = mapMeProfileToUserProfile(responseV3.data, POOL_NAME);
      const username = String(data.username ?? '').trim();

      if (username) {
        dispatch(
          addUserToAuth({
            username
          } as AuthState)
        );
      }

      await dispatch(
        fetchAuditableElements({
          applicationName: APP_NAME_PULSE_WEB
        })
      );
      return {
        status: responseV3.status,
        data: {
          ...data,
          username: username || data.username
        }
      };
    } catch (err) {
      return rejectWithValue(getErrorObject(err as ApiError));
    }
  }
);

export const fetchUpdateUser = createAsyncThunk(
  'userProfile/updateUser',
  async (
    { updatedData }: { updatedData?: {} },
    { getState, rejectWithValue }
  ) => {
    const state = getState() as {
      userProfile: UserProfile;
      auth: { accessToken: string };
    };
    const token = state.auth.accessToken;

    const userProfile = state.userProfile;

    try {
      const updatedProfile = {
        ...userProfile,
        customAtributes: {
          ...userProfile.customAtributes,
          ...updatedData
        }
      };
      const response = await updateUserProfileServ(token, updatedProfile);
      return response;
    } catch (err) {
      return rejectWithValue(getErrorObject(err as ApiError));
    }
  }
);

export const fetchUpdateProfile = createAsyncThunk(
  'userProfile/updateProfileState',

  async ({ updatedData }: { updatedData?: {} }, { dispatch }) => {
    try {
      const result = await dispatch(
        fetchUpdateUser({
          updatedData
        })
      );

      await delay(1000);

      if (fetchUpdateUser.fulfilled.match(result)) {
        await dispatch(fetchUserProfile({}));
      }

      return true;
    } catch (error) {
      return error;
    }
  }
);

export const initialStateUser: UserProfile = {
  company: '',
  companyId: '',
  email: '',
  emailVerified: '',
  groups: [],
  isActive: '',
  name: '',
  phone: '',
  solarisId: '',
  sub: '',
  fleetIds: [],
  lastLoginAt: '',
  username: '',
  apps: [],
  attributes: {
    groups: []
  },
  customAtributes: {
    name: '',
    groups: []
  },
  status: {
    code: 0,
    message: ''
  },
  error: {
    description: '',
    code: 0
  }
};

export const userProfileSlice = createSlice({
  name: 'userProfile',
  initialState: initialStateUser,
  reducers: {
    getUserProfile: (state, action: PayloadAction<UserProfile>) => {
      return {
        ...state,
        ...action.payload
      };
    },
    clearUserProfile: () => initialStateUser,
    clearStatus: state => {
      state.status = initialStateUser.status;
      state.error = initialStateUser.error;
    }
  },
  extraReducers: builder => {
    builder
      // Get User Profile
      .addCase(fetchUserProfile.pending, state => {
        state.status = { code: 1, message: 'loading' };
        state.error = initialStateUser.error;
      })
      .addCase(fetchUserProfile.fulfilled, (state, action) => {
        state.status = {
          ...action.payload.status
        };
        Object.assign(state, action.payload.data);
      })
      .addCase(fetchUserProfile.rejected, (state, action) => {
        Object.assign(state, initialStateUser);
        const payload = action.payload as ApiError;
        state.status = { code: 401, message: 'failed' };
        state.error = {
          description: payload.technicalMessage ?? payload.description,
          code: payload.code
        };
      })

      // Update User Profile
      .addCase(fetchUpdateUser.pending, state => {
        state.status = { code: 1, message: 'loading' };
        state.error = initialStateUser.error;
      })
      .addCase(fetchUpdateUser.fulfilled, (state, action) => {
        // state.status = { code: 1, message: 'User Profile Updated' };
        Object.assign(state, action.payload);
      })
      .addCase(fetchUpdateUser.rejected, (state, action) => {
        const payload = action.payload as ApiError;
        state.status = { code: 401, message: 'failed' };
        state.error = {
          description: payload.technicalMessage ?? payload.description,
          code: payload.code
        };
      });
  }
});

// Action creators are generated for each case reducer function
export const { getUserProfile, clearUserProfile, clearStatus } =
  userProfileSlice.actions;
export default userProfileSlice.reducer;
